import Button from "../../../../../ui/Button/Button";
import { ScrapeStatus } from "../../../../../__generated__/gql/graphql";
import { useDisclosure } from "../../../../../ui/util/useDisclosure";
import ScrapeWebpagesModal from "../ScrapeWebpagesModal/ScrapeWebpagesModal";
import { formatDistance } from "date-fns";
import { graphql } from "../../../../../__generated__/gql";
import { useMutation } from "urql";
import styles from "./RequestScrape.module.scss";
import { useEffect, useMemo, useState } from "react";
import Feedback from "../../../../../ui/Feedback/Feedback";
import Tooltip from "../../../../../ui/Tooltip/Tooltip";
import { Info } from "../../../../../ui/Icon/Icon";
import SaveChangesModal from "../SaveChangesModal/SaveChangesModal";
import useUser from "../../../../../providers/UserProvider/useUser";

const ScrapeWebsitesMutation = graphql(/* GraphQL */ `
  mutation scrapeWebsites {
    scrapeWebsites {
      websites
      lastScrapedAt
      scrapeStatus
      excludePathsFromReference
      excludePagesFromReference
      excludePathsFromInternalLinking
      excludePagesFromInternalLinking
    }
  }
`);

function LastScrapedMessage({
  lastScrapedAt,
  scrapeStatus,
}: {
  lastScrapedAt?: string;
  scrapeStatus?: ScrapeStatus;
}) {
  const [now, setNow] = useState(new Date());

  const intervalString = useMemo(() => {
    if (!lastScrapedAt) {
      return undefined;
    }

    return `${formatDistance(new Date(lastScrapedAt), now, {
      addSuffix: false,
    })} ago`;
  }, [lastScrapedAt, now]);

  useEffect(() => {
    const interval = setInterval(() => setNow(new Date()), 30000);

    return () => clearInterval(interval);
  }, []);

  if (!lastScrapedAt) {
    return null;
  }

  if (scrapeStatus === ScrapeStatus.Processing) {
    return <span>Currently scraping...</span>;
  }

  return <span>{`Last scrape ${intervalString}.`}</span>;
}

function RequestScrape({
  lastScrapedAt,
  scrapedWebpagesCount,
  scrapeStatus,
  disabled,
  isDirty,
}: {
  lastScrapedAt?: string;
  scrapedWebpagesCount?: number;
  scrapeStatus?: ScrapeStatus;
  disabled?: boolean;
  isDirty: boolean;
}) {
  const { billingInfo } = useUser();
  const [scrapedWebpagesModalOpened, scrapedWebpagesModal] = useDisclosure();
  const [saveChangesModalOpened, saveChangesModal] = useDisclosure();
  const [scrapeWebsitesResponse, scrapeWebsites] = useMutation(
    ScrapeWebsitesMutation,
  );

  return (
    <>
      <div className={styles.buttonWrapper}>
        <Button
          variant="primary"
          size="small"
          onClick={() => scrapeWebsites({})}
          disabled={
            disabled ||
            scrapeStatus === ScrapeStatus.Processing ||
            scrapeWebsitesResponse.fetching
          }
          loading={scrapeWebsitesResponse.fetching}
        >
          Request new scrape
        </Button>

        <Tooltip
          content={`A maximum of ${billingInfo.scrapeLimit} pages will be scraped across all websites. Use the filtering below if your websites contain more than this.`}
        >
          <span>
            <Info />
          </span>
        </Tooltip>
      </div>
      {scrapeStatus === ScrapeStatus.Error && (
        <Feedback>Something went wrong</Feedback>
      )}
      <div className={styles.scrapeDescription}>
        <LastScrapedMessage
          lastScrapedAt={lastScrapedAt}
          scrapeStatus={scrapeStatus}
        />

        {scrapeStatus === ScrapeStatus.Finished &&
          (scrapedWebpagesCount && scrapedWebpagesCount > 0 ? (
            <>
              <Button
                onClick={
                  isDirty ? saveChangesModal.open : scrapedWebpagesModal.open
                }
                variant="link"
              >
                View scraped webpages
              </Button>
              <SaveChangesModal
                isOpen={saveChangesModalOpened}
                onClose={saveChangesModal.close}
              />
              <ScrapeWebpagesModal
                isOpen={scrapedWebpagesModalOpened}
                onClose={scrapedWebpagesModal.close}
              />
            </>
          ) : (
            <Feedback>No webpages found</Feedback>
          ))}
      </div>
    </>
  );
}

export default RequestScrape;
